import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { surveysService } from "../services/surveys";
import { useAuthStore } from "../store/authStore";

export default function SurveysPage() {
  const { user } = useAuthStore();
  const [visibleCount, setVisibleCount] = useState(20); // 4 columns × 5 rows = 20

  const { data, isLoading, error } = useQuery({
    queryKey: ["surveys", "active", "funded"],
    queryFn: () => surveysService.list({ status: "active,funded", exclude_responded: "false" }),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-400">Loading surveys...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <p className="text-red-400">Error loading surveys: {(error as Error).message}</p>
      </div>
    );
  }

  const surveys = data?.surveys || [];
  const fundedSurveys = surveys.filter(s => s.contract_survey_id && s.contract_survey_id !== "null");
  const visibleSurveys = fundedSurveys.slice(0, visibleCount);
  const hasMore = visibleCount < fundedSurveys.length;

  const showMore = () => {
    setVisibleCount(prev => prev + 20); // Show 5 more rows at a time
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-100">Surveys</h1>
        {(user?.role === "admin" || user?.role === "agent") && (
          <Link to="/surveys/create" className="btn btn-primary">
            Create Survey
          </Link>
        )}
      </div>

      {fundedSurveys.length === 0 ? (
        <div className="card text-center py-12">
          <div className="text-4xl mb-4">💰</div>
          <p className="text-gray-400 mb-4">No funded surveys available yet.</p>
          <p className="text-gray-500 text-sm">Surveys need to be funded with USDC before they appear here.</p>
        </div>
      ) : (
        <>
          {/* 4 column grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {visibleSurveys.map((survey) => (
              <Link
                key={survey.id}
                to={`/surveys/${survey.id}`}
                className="card hover:border-primary-500 transition-all"
              >
                <h3 className="text-lg font-bold text-gray-100 mb-2 line-clamp-2">{survey.title}</h3>
                {survey.description && (
                  <p className="text-gray-400 mb-3 text-sm line-clamp-2">{survey.description}</p>
                )}
                <div className="space-y-1 text-xs text-gray-500">
                  <p>Status: <span className="font-medium capitalize text-gray-300">{survey.status}</span></p>
                  <p>Budget: <span className="font-medium text-gray-300">{survey.budget} USDC</span></p>
                  <p>Responses: <span className="font-medium text-gray-300">{survey.actual_responses} / {survey.target_responses}</span></p>
                </div>
              </Link>
            ))}
          </div>

          {/* Show more button */}
          {hasMore && (
            <div className="text-center">
              <button
                onClick={showMore}
                className="btn btn-secondary"
              >
                Show More Surveys
              </button>
              <p className="text-xs text-gray-500 mt-2">
                Showing {Math.min(visibleCount, fundedSurveys.length)} of {fundedSurveys.length} surveys
              </p>
            </div>
          )}
        </>
      )}

      {surveys.length > 0 && fundedSurveys.length === 0 && (
        <div className="card text-center py-12 mt-6">
          <div className="text-4xl mb-4">💰</div>
          <p className="text-gray-400 mb-4">No funded surveys available yet.</p>
          <p className="text-gray-500 text-sm">Surveys need to be funded with USDC before they appear here.</p>
        </div>
      )}
    </div>
  );
}
